import gsap from 'gsap';
import type {
  StoryblokComponent,
  StoryblokResult,
  StoriesParams,
  StoryData,
} from 'storyblok-js-client';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useStoryBlokBridge } from 'util/storyblok/hooks/useStoryBlokBridge';
import { usePageTransition } from 'util/react-transition-component/hooks/usePageTransition';
import type { StoryPageProps } from 'util/storyblok/types/page.types';
import DynamicComponent from 'components/DynamicComponent';
import { fetchStory, getAllCases, getNextCase, getRecentNews } from 'util/storyblok/api';
import ParamName from '@/router/ParamName';
import { getPrerenderPaths } from 'util/storyblok/static-paths';
import { StorySlug } from 'util/storyblok/types/api.types';
import { isCrossFlow, isFadeTransition } from './[[...slug]].config';
import { useGlobalStore } from '../stores/global';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './[[...slug]].transitions';
import useThemeScroll from '@/hooks/useThemeScroll';
import type { ScrollProviderContextType } from '../components/unlisted/ScrollProvider';
import { useScrollProviderContext } from '../components/unlisted/ScrollProvider';
import { noop } from 'util/react-transition-component/lib/noop';
import type { FlowProviderContextType } from '../util/react-transition-component/components/FlowProvider';
import { useFlowProviderContext } from '../util/react-transition-component/components/FlowProvider';
import { useRouter } from 'next/router';
import { isDesktop } from '../hooks/useIsDesktopDevice';
import type { FeaturedNewsOverviewBlokData } from '../components/blocks/featured-news-overview/FeaturedNewsOverview.data';
import type { AssetLink } from '../util/storyblok/types/global.types';
import type { ThemeColorProviderContextType } from '../components/unlisted/ThemeColorProvider';
import { useThemeColorProviderContext } from '../components/unlisted/ThemeColorProvider';
import type { ArticleHeaderBlokData } from '../components/organisms/article-header/ArticleHeader.data';
import { NewsCategoryLabelsProvider } from '../context/newsCategoryLabelsContext';
import {
  getNewsCategoryLabels,
  hasNewsCategories,
  NEWS_CATEGORY_CACHE_SECONDS,
} from '../util/storyblok/news-categories';
// import { isEditor } from '../util/storyblok/storybridge.utils';

export type DynamicPageProps = {
  className?: string;
} & StoryPageProps;

export const getStoryParams = (storyName: string): StoriesParams => {
  const params: StoriesParams = {
    resolve_relations:
      'case_card.case,featured_news_item.news,case.locations,case_listing.locations_filter,locations_blok.quick_links,related_news.news_items',
  };
  switch (storyName) {
    case 'asset-list':
      params.resolve_relations = 'case_card.case,featured_news_item.news,related_news.news_items';
      break;
    case StorySlug.People:
      params.resolve_links = 'url';
      break;
    default:
      break;
  }

  return params;
};

const DynamicPage: NextPage<DynamicPageProps> = ({ nextStory, recentNews, allCases, ...props }) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const { flowHijacked } = useFlowProviderContext() as FlowProviderContextType;
  const story = useStoryBlokBridge(props.story, props.preview, props.locale);
  const router = useRouter();

  const store = useGlobalStore();
  const [canTransitionInChild, setCanTransitionInChild] = useState<boolean>(false);
  const hasCrossFlow = useCallback(
    () => isCrossFlow(store.toPath, store.fromPath),
    [store.fromPath, store.toPath],
  );
  const hasFadeTransition = useCallback(
    () => isFadeTransition(store.toPath, store.fromPath),
    [store.fromPath, store.toPath],
  );
  const pageRenderDelayedCall = useRef<gsap.core.Tween>(undefined);

  const { scrollWrapperRef, unLock, scrollTo, lock, scrollStatusRef } =
    useScrollProviderContext() as ScrollProviderContextType;

  // Store featured-news-items from news-overview page, so we can use this to filter out duplicated items for the news-overview-block below.
  const featuredNews = story?.content?.modules?.find(
    (module) => module.component === 'featured_news_overview',
  ) as unknown as FeaturedNewsOverviewBlokData;
  if (featuredNews && featuredNews?.news_items?.length > 0) {
    store.setFeaturedNews(featuredNews?.news_items);
  }

  store.setRecentNewsData(recentNews);
  store.setNextStoryData(nextStory);
  if (allCases) {
    store.setAllCases(allCases);
  }

  const { setForceRefresh, setPrimaryThemeColor } =
    useThemeColorProviderContext() as ThemeColorProviderContextType;

  setPrimaryThemeColor(
    story?.content?.main_color_background?.color,
    story?.content?.main_color_foreground?.color,
  );

  useThemeScroll(
    story?.content?.is_dark_theme,
    story?.content?.accent_color_foreground?.color,
    story?.content?.accent_color_background?.color,
    props.preview ? story : undefined,
  );

  const lockBody = useCallback((lock: boolean) => {
    // Prevent/force-stop inertia/momentum scrolling on touch-devices when scrolling from case-to-case-page.
    if (!isDesktop) {
      document.body.style.maxHeight = lock ? '100vh' : 'unset';
      document.body.style.overflow = lock ? 'hidden' : 'auto';
    }
  }, []);

  usePageTransition({
    beforeTransitionOut(release) {
      store.setMenuOpenState(false);
      setCanTransitionInChild(false);

      if (!hasCrossFlow() && !hasFadeTransition()) {
        store.coverPageWipeEffect(true);
      }

      // Make navigation header visible
      scrollTo(0, (scrollStatusRef?.current?.offset?.y || 0) - 1, 0);
      release();
    },
    onDispose() {
      pageRenderDelayedCall?.current?.kill();
    },
    beforeTransitionIn: async (release) => {
      lockBody(true);

      // Start page at top.
      scrollTo(0, 0, 0);
      lock();

      // If First landing.
      if (!store.fromPath) {
        // Give some time to render-page/execute JS before triggering animations.
        pageRenderDelayedCall.current = gsap.delayedCall(0.5, () => noop());
        await pageRenderDelayedCall.current;
      }

      if (store.showPageWipeEffect) {
        await gsap.delayedCall(0.4, () => store.coverPageWipeEffect(false));
      }

      release();

      gsap.delayedCall(isDesktop ? 0 : 0.1, async () => {
        await flowHijacked.current;
        setCanTransitionInChild(true);

        unLock();
        lockBody(false);

        // Start page at top. (force again, in case the scrolling continued on touch-devices).
        scrollTo(0, 0, 0);

        // Scroll-to element with #hash ID.
        const hash = router.asPath.split('#')[1];
        if (hash) {
          const element = document.querySelector(`[data-scroll-to=${hash}]`);
          if (element) {
            scrollTo(0, element.getBoundingClientRect().top, 2000);
          }
        }
      });
    },
    crossFlow: () => hasCrossFlow(),
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        scrollWrapperRef,
        skipWipeIn: hasCrossFlow() || hasFadeTransition(),
        skipWipeOut: () => hasCrossFlow() || hasFadeTransition(),
        hasFadeTransition: () => hasFadeTransition(),
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  // The following useEffect aims to get rid of duplicated seo info inherited from globalSettings that confuse crawlers
  useEffect(() => {
    const nextData = document.querySelector('#__NEXT_DATA__');

    if (!nextData) return;

    const htmlObject = JSON.parse(nextData.innerHTML);
    if (
      htmlObject?.props?.pageProps?.story?.content?.seo_title &&
      htmlObject?.props?.globalSettings?.seo_title
    ) {
      delete htmlObject.props.globalSettings.seo_title;
    }

    if (
      htmlObject?.props?.pageProps?.story?.content?.seo_description &&
      htmlObject?.props?.globalSettings?.seo_description
    ) {
      delete htmlObject.props.globalSettings.seo_description;
    }

    nextData.innerHTML = JSON.stringify(htmlObject);

    setForceRefresh((x) => x + 1);
  }, [setForceRefresh]);

  return (
    <NewsCategoryLabelsProvider labels={props.newsCategoryLabels}>
      <div ref={elementRef} className={classNames(props.className)}>
        <DynamicComponent
          blok={{ ...story.content, published_at: story.published_at, tag_list: story.tag_list }}
          canTransitionIn={canTransitionInChild}
        />
      </div>
    </NewsCategoryLabelsProvider>
  );
};

export type PageParams = {
  // Use the name of the file when it's an dynamic page ([].tsx)
  [ParamName.Slug]?: Array<string> | string;
};

export const getStaticProps: GetStaticProps<DynamicPageProps, PageParams> = async (context) => {
  let response!: StoryblokResult;
  const slugs = context?.params?.slug;
  let storyName;
  let fullSlug;
  let recentNewsResponse;
  let nextStory;
  let allCases;

  if (slugs && slugs.length > 0) {
    fullSlug = `${(slugs as Array<string>).join('/')}`;

    storyName = slugs[0];
  }

  fullSlug = fullSlug || StorySlug.Home;
  storyName = storyName || StorySlug.Home;

  // Helper to interpret errors from Storyblok / fetchStory
  const handleFetchError = (error: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyError = error as any;
    const status =
      anyError?.response?.status !== undefined
        ? anyError.response.status
        : anyError?.status !== undefined
          ? anyError.status
          : undefined;

    // Only treat true 404 as notFound
    if (status === 404) {
      return {
        props: { story: false, ...context },
        notFound: true as const,
      };
    }

    // Any other error should bubble up so revalidation fails
    // and Next.js keeps the last successfully generated page.
    if (error instanceof Error) {
      throw error;
    }

    throw new Error(`Failed to fetch page: ${String(error)}`);
  };

  // Redirect to storyblok-pdf asset if we have a match on asset-list/name-of-pdf.
  if (storyName === 'asset-list') {
    try {
      response = await fetchStory(
        storyName,
        {
          ...getStoryParams(storyName),
          language: context?.locale,
        },
        context.preview,
      );

      const assetList = response.data?.story?.content?.modules?.find(
        (block: StoryblokComponent<string>) => block.component === 'asset_list',
      );

      if (assetList && slugs && slugs?.length > 0) {
        const filename = slugs[1];
        const match = assetList?.assets?.find((item: AssetLink) => item.title === filename);

        if (match?.asset?.filename) {
          return {
            redirect: {
              destination: match.asset.filename,
              statusCode: 301,
            },
            revalidate: 3600,
          };
        }
      }

      // No matching asset -> 404 notFound: true is correct
      return {
        props: { story: false, ...context },
        notFound: true,
        revalidate: 3600,
      };
    } catch (error) {
      return handleFetchError(error);
    }
  }

  // Get Page Data
  try {
    response = await fetchStory(
      fullSlug,
      {
        ...getStoryParams(storyName),
        language: context?.locale,
      },
      context.preview,
    );
  } catch (error) {
    return handleFetchError(error);
  }

  let recentNewsPath: string = StorySlug.News;

  if (fullSlug) {
    switch (storyName) {
      case StorySlug.News: {
        const params: {
          per_page?: number;
          language?: string;
        } = {
          language: context?.locale,
        };

        // News Detail page
        if (fullSlug !== storyName && fullSlug !== 'news/news-overview-mock-up') {
          params.per_page = 3;
        }

        recentNewsResponse = await getRecentNews(fullSlug, params, context.preview);
        break;
      }

      case StorySlug.Cases: {
        nextStory = await getNextCase(
          response?.data?.story?.uuid,
          {
            language: context?.locale,
          },
          fullSlug,
          context.preview,
        );
        break;
      }

      case StorySlug.Location: {
        if (slugs && slugs.length > 0) {
          const officeSlug = slugs[1];
          const folderName = slugs[2];
          recentNewsPath = `${StorySlug.Location}/${officeSlug}/${StorySlug.News}`;

          if (folderName) {
            switch (folderName) {
              case StorySlug.Cases:
                // We're not fetching nextStory anymore when in location folder
                break;

              case StorySlug.News:
                recentNewsResponse = await getRecentNews(
                  fullSlug,
                  {
                    language: context?.locale,
                    per_page: 3,
                  },
                  context.preview,
                  recentNewsPath,
                );
                break;

              default:
            }
          } else if (officeSlug) {
            recentNewsResponse = await getRecentNews(
              fullSlug,
              {
                language: context?.locale,
                per_page: 3,
              },
              context.preview,
            );
          }
        }
        break;
      }

      case StorySlug.Work: {
        allCases = await getAllCases({ language: context.locale }, context.preview);
        break;
      }

      default:
    }
  }

  // Fetch latest news-items if we have this module on the page
  const latestNewsModule = response.data?.story?.content?.modules?.find(
    (block: StoryblokComponent<string>) => block.component === 'latest_news',
  );

  if (!!latestNewsModule && !recentNewsResponse) {
    recentNewsResponse = await getRecentNews(
      fullSlug,
      {
        language: context?.locale,
        per_page: latestNewsModule.per_page,
      },
      context.preview,
      recentNewsPath,
    );
  }

  const needsNewsCategories = hasNewsCategories(response.data?.story?.content);
  // Preview pages need labels before an editor adds the first news block through
  // the bridge, so translations are available without a reload or a client-side token.
  const newsCategoryLabels =
    needsNewsCategories || context.preview
      ? await getNewsCategoryLabels(
          context.locale,
          context.preview || process.env.NODE_ENV === 'development',
        )
      : null;

  return {
    props: {
      story: response?.data?.story ?? false,
      newsCategoryLabels,
      recentNews:
        recentNewsResponse?.data?.stories?.filter(
          (story: StoryData<ArticleHeaderBlokData>) => !story?.content?.hide_article,
        ) ?? false,
      nextStory: nextStory ?? false,
      allCases: allCases ?? false,
      ...context,
    },
    revalidate:
      needsNewsCategories && context.locale !== context.defaultLocale
        ? NEWS_CATEGORY_CACHE_SECONDS
        : 3600,
  };
};

export const getStaticPaths: GetStaticPaths<PageParams> = async () => {
  return {
    paths: await getPrerenderPaths(),
    fallback: 'blocking',
  };
};
export default DynamicPage;
